import { isAccepted, isWithdrawn } from './registrationTeamStatus';

export type WaiverStatus = 'signed' | 'not_signed';
export type PlayerWaiverStatus = 'signed' | 'pending';
export type ApplicationStatus = 'withdrawn' | 'accepted' | 'received' | 'incomplete';
export type PaymentStatus = 'free' | 'paid' | 'partial' | 'overpaid' | 'unpaid' | 'paid_manually';

/**
 * Determines team waiver/release-statement signing status.
 * A waiver is considered signed when all required fields are present.
 */
export function getWaiverStatus(team: any): WaiverStatus {
  const signed = team?.signed;
  if (signed && signed.date && signed.checked && signed.title && signed.fullName) {
    return 'signed';
  }
  return 'not_signed';
}

/**
 * Determines an individual player's waiver status from their waiverDetails array.
 */
export function getPlayerWaiverStatus(player: any): PlayerWaiverStatus {
  const waiverDetails = player?.waiverDetails;
  if (Array.isArray(waiverDetails) && waiverDetails.some((w: any) => w.accepted === true)) {
    return 'signed';
  }
  return 'pending';
}

/**
 * Determines the overall application status for a registration team.
 */
export function getApplicationStatus(team: any): ApplicationStatus {
  // Withdrawn wins outright — a team that pulled out shouldn't be reported back
  // to its manager as "Accepted" or "Application Received" just because it once
  // was, or because its waiver is still on file.
  if (isWithdrawn(team)) {
    return 'withdrawn';
  }
  if (isAccepted(team)) {
    return 'accepted';
  }
  if (getWaiverStatus(team) === 'signed') {
    return 'received';
  }
  return 'incomplete';
}

/**
 * Resolves the effective payment product for a team by checking the division,
 * contest, and tournament tiers' `config.paymentProduct` (ADR 0034 — the fee is
 * a config-hierarchy field; the registration is NOT a tier).
 */
export function resolvePaymentProduct(team: any): any {
  // A populated division/contest may be a TOMBSTONE. The registrant-facing reads
  // (`findById`, `findByLeagueName`) opt into `withDeleted` so the pages can show
  // what the team originally requested — but pricing must NOT follow a deleted
  // entity, or the public page quotes a price the registrant was never charged.
  // Mirrors `liveConfigProduct` in registrationTeam.controller.ts, which guards
  // the server-side equivalent (`getPaymentProductId`). See ADR 0028.
  const live = (entity: any) => (entity && entity.deleted === true ? undefined : entity?.config?.paymentProduct);

  // `config.paymentProduct` is ONE hierarchy field — a ref to a single product —
  // so the most-specific live non-null level wins WHOLESALE, exactly like the
  // server charge path (`getConfigProductId` in registrationTeam.controller.ts).
  // Never merge levels key-by-key: blending a division product's `amount` with a
  // parent product's `perPlayer` quotes a hybrid price no product carries and
  // diverges from what checkout actually charges.
  const product =
    live(team?.requestedDivision) ||
    live(team?.requestedContest) ||
    team?.tournament?.config?.paymentProduct;

  if (!product || typeof product !== 'object') {
    // Nothing configured, or an unpopulated ref (bare id) we can't price from.
    return {};
  }
  return { ...product };
}

/**
 * Calculates the total payment due in cents.
 * @param paymentProduct - the resolved payment product (amount is in cents)
 * @param playerCount - number of registered players
 * @param customPaidAmountDollars - optional manual credit in dollars (converted to cents internally)
 */
export function calculatePaymentDue(
  paymentProduct: any,
  playerCount: number,
  customPaidAmountDollars?: number,
): number {
  let totalCost = 0;
  const amount = paymentProduct?.amount;

  if (amount) {
    totalCost = paymentProduct?.perPlayer ? amount * playerCount : amount;
  }

  if (customPaidAmountDollars && customPaidAmountDollars > 0) {
    totalCost = Math.max(0, totalCost - (customPaidAmountDollars * 100));
  }

  return totalCost;
}

/**
 * Calculates the total payment made in cents.
 * Combines Stripe payment records with any manually logged custom amount.
 * @param stripePaymentsCents - sum of Stripe payment records in cents
 * @param team - registrationTeam object (uses team.customPaidAmount, stored in dollars)
 */
export function calculateTotalPaymentMade(stripePaymentsCents: number, team: any): number {
  const customPaidCents =
    team?.customPaidAmount && team.customPaidAmount > 0 ? team.customPaidAmount * 100 : 0;
  return stripePaymentsCents + customPaidCents;
}

/**
 * Determines payment status by comparing amount due vs amount paid.
 * All amounts are in cents.
 * @param paymentDue - total cost in cents
 * @param paymentMade - total payments made in cents
 * @param hasPaid - manual "has paid" flag set by admin
 */
export function getPaymentStatus(
  paymentDue: number,
  paymentMade: number,
  hasPaid?: boolean,
): PaymentStatus {
  if (paymentDue === 0) {
    return 'free';
  }

  if (paymentMade > 0 && paymentDue > 0) {
    if (paymentMade > paymentDue) return 'overpaid';
    if (paymentMade === paymentDue) return 'paid';
    return 'partial';
  }

  if (hasPaid) {
    return 'paid_manually';
  }

  return 'unpaid';
}
